import { lastValueFrom } from "rxjs";
import { createEffect, createResource, createSignal, For } from "solid-js";

import { VideosEditor } from "./VideosEditor";
import type { UtdoVideo } from "../types";
import { action } from "../util/action";
import { useApi } from "../util/api";
import { panic } from "../util/panic";
import { success } from "../util/success";

export function Videos() {
    const api = useApi();
    const [ currentId, setCurrentId ] = createSignal<number | undefined>(undefined);

    const [ videos, { refetch } ] = createResource(
        async () => {
            try {
                return await lastValueFrom(api.selectVideos());
            } catch (err: any) {
                panic(err);
            }
            return [] as UtdoVideo[];
        },
        { initialValue: [] as UtdoVideo[] }
    );

    let dialogRef!: HTMLDialogElement;

    function currentVideo(): UtdoVideo {
        const id = currentId();
        return videos().find((v) => v.id === id) ?? {
            id: 0,
            title: "",
            url: "",
            sort: 0,
            hidden: true,
        };
    }

    createEffect(() => {
        const id = currentId();
        if (id === undefined) {
            dialogRef.close();
            return;
        }
        dialogRef.showModal();
    });

    const editVideo = (id?: number) => (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        setCurrentId(id)
    };

    const save = async (video: UtdoVideo) => {
        try {
            if ((video.id ?? 0) > 0) {
                if (await lastValueFrom(api.updateVideo(video))) {
                    success("Vidéo enregistrée avec succès.");
                }
            } else {
                if (await lastValueFrom(api.insertVideo(video))) {
                    success("Vidéo enregistée avec succès.");
                }
            }
            setCurrentId(undefined);
            refetch();
        } catch (err: any) {
            panic(err);
        }
    };
    const deleteVideo = action(
        async (v: UtdoVideo) => {
            if (!confirm(`Vous allez supprimer la vidéo "${v.title}".\nContinuer ?`)) {
                return;
            }
            await lastValueFrom(api.deleteVideo(v.id!));
        },
        refetch,
    );
    const moveDown = action(
        async (v: UtdoVideo) => await lastValueFrom(api.moveVideoDown(v.id!)),
        refetch
    );
    const moveUp = action(
        async (v: UtdoVideo) => await lastValueFrom(api.moveVideoUp(v.id!)),
        refetch
    );
    const toggleVisibility = action(
        async (v: UtdoVideo) => await lastValueFrom(api.toggleVideoVisibility(v.id!)),
        refetch
    );

    return <section id="admin-videos">
        <h2>Vidéos</h2>
        <div>
            <div class="actions">
                <button onClick={editVideo(0)}>Nouvelle vidéo</button>
            </div>
            <ul>
                <For each={videos()}>
                    {(v) => <li>
                        <a href="#"
                            onClick={editVideo(v.id!)}>{v.title}</a>
                        <div class="actions">
                            <a href="#" class="icon"
                                onClick={moveUp(v)}>arrow_upward</a>
                            <a href="#" class="icon"
                                onClick={moveDown(v)}>arrow_downward</a>
                            <a href="#" class="icon"
                                onClick={toggleVisibility(v)}>
                                {
                                    v.hidden
                                        ? "visibility_off"
                                        : "visibility"
                                }
                            </a>
                            <a href="#" class="icon"
                                onClick={deleteVideo(v)}>delete</a>
                        </div>
                    </li>}
                </For>
            </ul>
        </div>
        <dialog ref={dialogRef}>
            <VideosEditor
                video={currentVideo()}
                validate={save}
                cancel={() => setCurrentId(undefined)}
                />
        </dialog>
    </section>;
}